/**
 * Animation Controller - Halftone Theme
 * IntersectionObserver-based scroll-triggered animations
 * Respects user preferences and theme settings
 */

/**
 * The elements theme.css gives the on-load keyframe entrance, as ONE selector
 * that both files share — read it alongside the on-load block in theme.css and
 * change them together.
 *
 * Written as a selector rather than reimplemented in JS on purpose. The two
 * only have to disagree once to hide content: an element the CSS stops
 * revealing is left at opacity 0 by the rules above it, and if JS also skips it
 * nothing brings it back. Walking main.children and taking the first two that
 * are sections looks equivalent to `:nth-child(-n + 2)` and is not — they part
 * company the moment anything that is not a section is a child of
 * #main-content.
 *
 * Scope: the first two sections of the template. #main-content holds
 * content_for_layout and nothing else, and both section groups render outside
 * it (theme.liquid), so the header and footer groups can never match. That is
 * containment, not an index — section.index restarts at 1 inside every group,
 * which is how the footer group ended up playing a page-load entrance on
 * Capital (weareeight/Theme--Capital#529).
 *
 * Cap: repeated-grid items past the fourth are NOT part of the entrance. A grid
 * is a queue of below-the-fold content, and treating all of it as above the
 * fold cost 4 accessibility points on collection (#328).
 */
const ON_LOAD_CAP =
  ':not(:is(.collection-products__item, .featured-collection__item, ' +
  '.collections-grid__item, .blog-posts__item, .collection-list__item, ' +
  '.image-gallery__item, .search-results__item):nth-child(n + 5))';

const ON_LOAD_SELECTOR =
  '#main-content > .shopify-section:nth-child(-n + 2) [data-animate]' + ON_LOAD_CAP;

class AnimationController {
  constructor(options = {}) {
    this.options = {
      threshold: 0.05,  // Lower threshold for earlier triggering
      rootMargin: '50px 0px -30px 0px',  // Added top margin for elements entering from above
      ...options
    };

    this.observer = null;
    this.initialized = false;

    // Reveal elements need scroll-based detection because clip-path
    // creates 0 visible area, preventing IntersectionObserver from working
    this.revealElements = [];
    this.scrollHandler = null;
    this.ticking = false;
  }

  /**
   * Initialize the animation controller
   * Call this after DOM is ready
   */
  init() {
    // Check if animations are disabled
    if (this.shouldDisableAnimations()) {
      this.showAllElements();

      // The theme editor sets `animations-disabled`, so the scroll system stays
      // off and the editor stays calm. Section headings are the exception: a
      // merchant choosing a heading's Fade reveal needs to see it, the same way
      // the typed reveal already previews. Replay it for preview-marked headings.
      if (this.isDesignMode() && !this.prefersReducedMotion()) {
        this.setupEditorHeadingPreview();
      }
      return;
    }

    // Create IntersectionObserver
    this.observer = new IntersectionObserver(
      this.handleIntersect.bind(this),
      {
        threshold: this.options.threshold,
        rootMargin: this.options.rootMargin
      }
    );

    // Observe all animated elements
    this.observeElements();
    this.initialized = true;

    // Setup scroll-based detection for reveal elements (clip-path workaround)
    this.setupScrollListener();

    // Re-observe on Shopify section events (theme editor)
    this.setupSectionEvents();

    // Reveal on focus, so keyboard users never land on an invisible element
    this.setupFocusReveal();
  }

  /**
   * An un-revealed [data-animate] element is opacity: 0, but everything inside
   * it is still in the tab order. Tabbing ahead of the scroll position therefore
   * put focus on links and buttons that were not on screen, with no visible
   * indicator anywhere - the "invisible focus stops" and "ghost elements" in
   * Shopify's review.
   *
   * Revealing on focus rather than stripping tabindex keeps the content
   * reachable and keeps the fix in one place: whatever the shopper is focused on
   * is, by definition, visible. focusin bubbles, so one listener covers the page
   * including markup swapped in later.
   */
  setupFocusReveal() {
    document.addEventListener('focusin', (event) => {
      let el = event.target;
      while (el && el !== document.body) {
        if (el.matches && el.matches('[data-animate]:not(.is-visible)')) {
          el.classList.add('is-visible');
          if (this.observer) this.observer.unobserve(el);
          this.revealElements = this.revealElements.filter(item => item !== el);
        }
        el = el.parentElement;
      }
    });
  }

  /**
   * Check if element uses clip-path reveal animation
   * These need scroll-based detection instead of IntersectionObserver
   */
  isRevealElement(el) {
    const animType = el.dataset.animate || '';
    return animType.startsWith('reveal');
  }

  /**
   * Setup scroll listener for reveal elements
   * Uses requestAnimationFrame for performance
   */
  setupScrollListener() {
    if (this.revealElements.length === 0) {
      return;
    }

    this.scrollHandler = () => {
      if (!this.ticking) {
        requestAnimationFrame(() => {
          this.checkRevealElements();
          this.ticking = false;
        });
        this.ticking = true;
      }
    };

    window.addEventListener('scroll', this.scrollHandler, { passive: true });

    // Initial check in case elements are already in view
    this.checkRevealElements();
  }

  /**
   * Check reveal elements on scroll and trigger animations
   */
  checkRevealElements() {
    if (this.revealElements.length === 0) {
      // All done, remove scroll listener
      if (this.scrollHandler) {
        window.removeEventListener('scroll', this.scrollHandler);
        this.scrollHandler = null;
      }
      return;
    }

    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    const triggerPoint = windowHeight * 0.85; // Trigger when element is 85% down the viewport

    // Check each reveal element
    this.revealElements = this.revealElements.filter(el => {
      const rect = el.getBoundingClientRect();

      // Trigger when top of element enters trigger zone
      if (rect.top < triggerPoint && rect.bottom > 0) {
        this.triggerAnimation(el);
        return false; // Remove from array
      }
      return true; // Keep in array
    });
  }

  /**
   * Check if the visitor prefers reduced motion
   */
  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * Is the theme editor open? Section replay and the visibility backstop are
   * editor-only, so the storefront experience stays exactly as it was.
   */
  isDesignMode() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  /**
   * Check if animations should be disabled
   */
  shouldDisableAnimations() {
    // Check prefers-reduced-motion
    if (this.prefersReducedMotion()) {
      return true;
    }

    // Check theme setting
    const body = document.body;
    if (body.classList.contains('animations-disabled')) {
      return true;
    }

    return false;
  }

  /**
   * Show all elements immediately (for when animations disabled)
   */
  showAllElements() {
    document.querySelectorAll('[data-animate]').forEach(el => {
      el.classList.add('is-visible');
    });
  }

  /**
   * Observe all elements with data-animate attribute
   */
  observeElements() {
    const elements = document.querySelectorAll('[data-animate]:not(.is-visible)');
    const onLoad = this.onLoadElements();

    elements.forEach((el) => {
      // Above the fold (#328): theme.css already played this element's entrance
      // from a keyframe at first paint, so it is on screen and settled by the
      // time this runs. Observing it would add is-visible to content the
      // shopper is already reading, which drops the keyframe and restarts the
      // entrance as a transition — a second, later fade over settled content.
      // Leaving it alone also leaves .is-visible free for setupFocusReveal and
      // replaySection, which both use it as the escape back to the JS path.
      if (onLoad.has(el)) {
        return;
      }

      const isReveal = this.isRevealElement(el);

      // Check if element is already in viewport (fixes above-the-fold elements)
      if (this.isInViewport(el)) {
        this.triggerAnimation(el);
      } else if (isReveal) {
        // Reveal elements use scroll-based detection (clip-path prevents IntersectionObserver)
        this.revealElements.push(el);
      } else {
        // Standard elements use IntersectionObserver
        this.observer.observe(el);
      }
    });
  }

  /**
   * The elements whose entrance theme.css plays from a keyframe at first paint:
   * every [data-animate] in the first two children of #main-content (which
   * holds content_for_layout and nothing else), minus the repeated-grid items
   * past the fourth.
   *
   * This MUST select exactly what the on-load block in theme.css selects. An
   * element this returns is already painted and settled, so observing it would
   * restart its entrance as a second, later fade. An element it wrongly
   * returned would be one theme.css never revealed and the observer then
   * skipped — invisible content. Both halves of the cap are written out in
   * ON_LOAD_CAP below; change them together.
   *
   * Off in the editor for the same reason the CSS is: replaySection() owns the
   * entrance there.
   */
  onLoadElements() {
    if (this.isDesignMode()) return new Set();

    return new Set(document.querySelectorAll(ON_LOAD_SELECTOR));
  }

  /**
   * Check if an element is currently in the viewport
   */
  isInViewport(el) {
    const rect = el.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    const windowWidth = window.innerWidth || document.documentElement.clientWidth;

    // Element is in viewport if any part of it is visible
    const vertInView = (rect.top <= windowHeight) && ((rect.top + rect.height) >= 0);
    const horInView = (rect.left <= windowWidth) && ((rect.left + rect.width) >= 0);

    return vertInView && horInView;
  }

  /**
   * Trigger animation for an element
   */
  triggerAnimation(el) {
    const delay = this.getDelay(el);

    if (delay > 0) {
      setTimeout(() => {
        el.classList.add('is-visible');
      }, delay);
    } else {
      el.classList.add('is-visible');
    }
  }

  /**
   * Handle intersection changes
   */
  handleIntersect(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        this.triggerAnimation(entry.target);
        // Stop observing this element
        this.observer.unobserve(entry.target);
      }
    });
  }

  /**
   * Get delay from element attributes or CSS variable
   */
  getDelay(el) {
    // Check data-animate-delay attribute (in ms)
    const dataDelay = el.dataset.animateDelay;
    if (dataDelay) {
      return parseInt(dataDelay, 10);
    }

    // Check --delay CSS custom property
    const style = el.style;
    if (style.getPropertyValue('--delay')) {
      return 0; // CSS handles the delay via transition-delay
    }

    return 0;
  }

  /**
   * Setup Shopify section events for the theme editor.
   *
   * These events only fire in design mode; on the storefront the handlers are
   * inert. In the editor we replay a section's load-in whenever it is
   * (re-)rendered or re-selected, so the merchant actually sees what the
   * section does on load rather than a static render.
   */
  setupSectionEvents() {
    const inEditor = this.isDesignMode();

    document.addEventListener('shopify:section:load', (e) => {
      const section = e.target;

      if (inEditor) {
        // A setting change re-renders the section; replay so the edit is seen.
        this.replaySection(section);
        return;
      }

      // Storefront fallback (defensive; this event is editor-only): observe any
      // newly added elements.
      section.querySelectorAll('[data-animate]:not(.is-visible)').forEach(el => {
        if (this.isInViewport(el)) {
          this.triggerAnimation(el);
        } else if (this.isRevealElement(el)) {
          if (!this.revealElements.includes(el)) {
            this.revealElements.push(el);
          }
          if (!this.scrollHandler) {
            this.setupScrollListener();
          }
        } else {
          this.observer.observe(el);
        }
      });
    });

    document.addEventListener('shopify:section:reorder', () => {
      // Re-observe all elements
      this.revealElements = []; // Reset reveal elements
      this.observeElements();
      // Re-setup scroll listener
      if (!this.scrollHandler && this.revealElements.length > 0) {
        this.setupScrollListener();
      }
    });

    document.addEventListener('shopify:section:select', (e) => {
      const section = e.target;

      if (inEditor) {
        // Re-selecting a section replays its load-in, even if it already ran —
        // the earlier "trigger only not-yet-run elements" showed nothing here.
        this.replaySection(section);
        return;
      }

      // Storefront fallback (defensive): trigger any not-yet-run visible els.
      section.querySelectorAll('[data-animate]:not(.is-visible)').forEach(el => {
        if (this.isInViewport(el)) {
          this.triggerAnimation(el);
        }
      });
    });
  }

  /**
   * Replay every animation in a section from its hidden start state.
   *
   * Editor-only: lets the merchant see the image wipe and staggered text each
   * time they add, edit, or re-select the section. Transitions are suppressed
   * while the elements are reset so the reset itself never animates (no reverse
   * wipe or fade-out), then restored before the load-in is triggered again.
   */
  replaySection(section) {
    if (!section || typeof section.querySelectorAll !== 'function') {
      return;
    }

    const elements = section.querySelectorAll('[data-animate]');
    if (!elements.length) {
      return;
    }

    elements.forEach(el => {
      // Drop any pending observation / scroll tracking for this element.
      if (this.observer) {
        this.observer.unobserve(el);
      }
      const index = this.revealElements.indexOf(el);
      if (index !== -1) {
        this.revealElements.splice(index, 1);
      }
      // Snap back to the start state without animating the reset.
      el.style.transition = 'none';
      el.classList.remove('is-visible');
    });

    // Force a reflow so the hidden state is committed before transitions resume.
    void section.offsetWidth;

    elements.forEach(el => {
      el.style.transition = '';
      this.triggerAnimation(el);
    });

    // Backstop: nothing in this section may linger invisible in the editor.
    this.scheduleVisibilityFallback(section);
  }

  /**
   * Editor safety net — a beat after a replay, force any element still hidden
   * to its visible state, so a re-render can never strand a section at
   * opacity: 0. No-op outside the editor, so storefront scroll animations that
   * legitimately wait below the fold are untouched.
   */
  scheduleVisibilityFallback(scope) {
    if (!this.isDesignMode()) {
      return;
    }
    setTimeout(() => {
      scope.querySelectorAll('[data-animate]:not(.is-visible)').forEach(el => {
        el.classList.add('is-visible');
      });
    }, 1000);
  }

  /**
   * Theme editor: preview a section heading's Fade reveal.
   *
   * In design mode the body is `animations-disabled`, so the scroll system is
   * off and showAllElements() has already revealed everything for a calm editor.
   * A heading marked data-animate-preview is the exception — it should still play
   * its fade so a merchant can see the Fade / None choice. A setting change
   * re-renders the section (shopify:section:load) with a fresh, un-revealed
   * heading; reveal it a couple of frames later so the transition runs from the
   * start. Matches the typed preview, which also replays on section load.
   */
  setupEditorHeadingPreview() {
    const reveal = (scope) => {
      scope.querySelectorAll('[data-animate][data-animate-preview]').forEach(el => {
        // Reset, then reveal on a later frame so the fade runs from scratch.
        el.classList.remove('is-visible');
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            el.classList.add('is-visible');
          });
        });
      });
    };

    document.addEventListener('shopify:section:load', (e) => reveal(e.target));
  }

  /**
   * Manually trigger animation for an element
   */
  animate(element) {
    if (element && !element.classList.contains('is-visible')) {
      this.triggerAnimation(element);
      if (this.observer) {
        this.observer.unobserve(element);
      }
    }
  }

  /**
   * Reset an element to its pre-animated state
   */
  reset(element) {
    if (element) {
      element.classList.remove('is-visible');
      if (this.initialized) {
        if (this.isRevealElement(element)) {
          // Add back to reveal elements for scroll detection
          if (!this.revealElements.includes(element)) {
            this.revealElements.push(element);
          }
          // Re-setup scroll listener if needed
          if (!this.scrollHandler) {
            this.setupScrollListener();
          }
        } else if (this.observer) {
          this.observer.observe(element);
        }
      }
    }
  }

  /**
   * Disconnect the observer (cleanup)
   */
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
      this.scrollHandler = null;
    }
    this.revealElements = [];
    this.initialized = false;
  }
}

/**
 * Stagger utility - applies incremental delays to a list of elements
 * @param {NodeList|Array} elements - Elements to stagger
 * @param {number} baseDelay - Starting delay in ms (default: 0)
 * @param {number} increment - Delay increment per element in ms (uses --animate-stagger CSS var as default)
 */
function staggerElements(elements, baseDelay = 0, increment = null) {
  const staggerIncrement = increment || parseInt(
    getComputedStyle(document.documentElement).getPropertyValue('--animate-stagger') || '50',
    10
  );

  elements.forEach((el, index) => {
    const delay = baseDelay + (index * staggerIncrement);
    el.style.setProperty('--delay', `${delay}ms`);
    el.dataset.animateDelay = delay;
  });
}

/**
 * Apply stagger to grid items within a container
 * Calculates row-first ordering for natural cascade effect
 * @param {Element} container - Container element
 * @param {string} itemSelector - Selector for grid items
 * @param {number} columns - Number of columns (will auto-detect if not provided)
 */
function staggerGrid(container, itemSelector = '[data-animate]', columns = null) {
  const items = container.querySelectorAll(itemSelector);
  if (!items.length) return;

  // Try to detect columns from CSS grid
  if (!columns) {
    const gridStyle = getComputedStyle(container);
    const templateColumns = gridStyle.gridTemplateColumns;
    if (templateColumns && templateColumns !== 'none') {
      columns = templateColumns.split(' ').filter(s => s !== '').length;
    } else {
      columns = 4; // Default fallback
    }
  }

  const staggerIncrement = parseInt(
    getComputedStyle(document.documentElement).getPropertyValue('--animate-stagger') || '50',
    10
  );

  items.forEach((item, index) => {
    const row = Math.floor(index / columns);
    const col = index % columns;
    // Cascade: row + column creates diagonal effect
    const delay = (row + col) * staggerIncrement;
    item.style.setProperty('--delay', `${delay}ms`);
    item.dataset.animateDelay = delay;
  });
}

// Create global instance
const animationController = new AnimationController();

// Auto-initialize on DOM ready with a small delay to ensure layout is complete
function initAnimations() {
  // Use requestAnimationFrame to ensure rendering is complete
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      animationController.init();
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAnimations);
} else {
  initAnimations();
}

// Export for module usage and global access
window.animationController = animationController;
window.staggerElements = staggerElements;
window.staggerGrid = staggerGrid;
